import { useEffect, useCallback, useState } from "react";
import axios from "axios";
import {
  Layout,
  Page,
  Card,
  InlineGrid,
  Text,
  BlockStack,
  Button,
  Spinner,
  InlineStack,
  Link,
  Badge,
  Banner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchBusinessTripDetails,
  setDestroy,
} from "./business-trip-container.slice";
import { fetchCompanyDetails } from "containers/settings/settings-container.slice";
import BusinessTripViewSkeleton from "./business-trip-view-skeleton";
import { AppDispatch, RootState } from "configureStore";
import PrettyDate from "components/format/PrettyDate";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import { BusinessTrip } from "types/BusinessTrip";
import { API_URL } from "../../constants";
import BusinessTripExpenses from "containers/business-trip-expenses/business-trip-expenses";
import { getCountryName } from "constants/countries";
import { getBusinessTripStatusTone } from "utils/businessTripStatus";
import { ViewIcon } from "@shopify/polaris-icons";
import "./styles.scss";
import "components/stack-narrow-section-first/stack-narrow-section-first.scss";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import useIsPhone from "features/files/use-is-phone";
import { PHONE_PAGE_GUTTER } from "components/tw/pageGutter";

function BusinessTripContainer({ source = "" }: { source?: string }) {
  const { t } = useTranslation();
  const { companyId, tripId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const isPhone = useIsPhone();
  const { role } = useSelector((state: RootState) => state.authReducer);
  const { businessTripEntity, isLoading, isError, requestedTripId } =
    useSelector((state: RootState) => state.businessTripContainerReducer);
  const { companyEntity } = useSelector(
    (state: RootState) => state.companyContainerReducer
  );
  const [mainTemplateError, setMainTemplateError] = useState("");
  const [reportTemplateError, setReportTemplateError] = useState("");
  const [isGeneratingMain, setIsGeneratingMain] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const permissionsGroups = (role?.permissionsgroups ?? []) as string[];
  const resolveTemplateSource = (
    source?: "system" | "custom",
    templateId?: string
  ): "system" | "custom" => source || (templateId ? "custom" : "system");

  const loadData = useCallback(() => {
    if (companyId && tripId) {
      dispatch(fetchBusinessTripDetails({ companyId, tripId }));
    }
  }, [dispatch, companyId, tripId]);

  useEffect(() => {
    loadData();
    if (companyId && !companyEntity) {
      dispatch(fetchCompanyDetails({ companyId }));
    }
    return () => {
      dispatch(setDestroy());
    };
  }, [loadData]);

  const handleEditClick = () => {
    dispatch(setModalxComponent("BusinessTripForm"));
    dispatch(setModalxComponentProps({ id: tripId, source }));
  };

  const generateBusinessTripDocument = async (
    businessTripFileType: "main_trip" | "trip_report" | "leaves_during_trip"
  ) => {
    if (!companyId || !tripId) return false;
    const tripUser =
      businessTripEntity && typeof businessTripEntity.user === "object"
        ? businessTripEntity.user.id
        : (businessTripEntity?.user as string) || role?.user;
    const payload = {
      date: new Date().toISOString().split("T")[0],
      templateId: "",
      customerId: "",
      customFields: {},
      products: [],
      productGroups: [],
      user: tripUser,
      businessTrip: tripId,
      businessTripFileType,
      signersInfo: {
        contactSigners: [],
        companyUserSigner: role?.manager || "",
        nontechuser: "",
        signType: "Click",
        selectedTabUser: "",
      },
    };
    try {
      await axios.post(`${API_URL}/${companyId}/files/build-pdf`, payload);
      dispatch(fetchBusinessTripDetails({ companyId, tripId }));
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleBuildPdf = async () => {
    setMainTemplateError("");
    const mainSource = resolveTemplateSource(
      companyEntity?.businessTripMainTemplateSource,
      companyEntity?.businessTripMainTemplate
    );
    if (mainSource === "custom") {
      if (!companyEntity?.businessTripMainTemplate) {
        setMainTemplateError("business_trip_main_template_not_configured");
        return;
      }
      dispatch(setModalxComponent("BusinessTripDocumentForm"));
      dispatch(
        setModalxComponentProps({
          user: tripUser,
          subject: "user",
          signType: "Click",
          managerSigner: role?.manager,
          businessTrip: tripId,
          businessTripFileType: "main_trip",
          lockedTemplateId: companyEntity.businessTripMainTemplate,
        })
      );
      return;
    }
    setIsGeneratingMain(true);
    const ok = await generateBusinessTripDocument("main_trip");
    setIsGeneratingMain(false);
    if (!ok) setMainTemplateError("error");
  };

  const handleCreateReport = () => {
    dispatch(setModalxComponent("BusinessTripReportForm"));
    dispatch(
      setModalxComponentProps({
        tripId,
        trip: details,
      })
    );
  };

  const handleEditReport = () => {
    dispatch(setModalxComponent("BusinessTripReportForm"));
    dispatch(
      setModalxComponentProps({
        tripId,
        trip: details,
      })
    );
  };

  const handleGenerateReportDocument = async () => {
    setReportTemplateError("");
    const reportSource = resolveTemplateSource(
      companyEntity?.businessTripReportTemplateSource,
      companyEntity?.businessTripReportTemplate
    );
    if (reportSource === "custom") {
      if (!companyEntity?.businessTripReportTemplate) {
        setReportTemplateError("business_trip_report_template_not_configured");
        return;
      }
      dispatch(setModalxComponent("BusinessTripDocumentForm"));
      dispatch(
        setModalxComponentProps({
          user: tripUser,
          subject: "user",
          signType: "Click",
          managerSigner: role?.manager,
          businessTrip: tripId,
          businessTripFileType: "trip_report",
          lockedTemplateId: companyEntity.businessTripReportTemplate,
        })
      );
      return;
    }
    setIsGeneratingReport(true);
    const ok = await generateBusinessTripDocument("trip_report");
    setIsGeneratingReport(false);
    if (!ok) setReportTemplateError("error");
  };

  // The loaded trip matches the route only when its id equals tripId — guards
  // against briefly showing the previous trip's data after navigating to a
  // different one (the route reconciles instead of remounting).
  const isTripReady = !!businessTripEntity && businessTripEntity.id === tripId;

  const backUrl =
    source === "profile"
      ? `/app/${companyId}/profile/${role?.user}/business-trips`
      : `/app/${companyId}/business-trips`;

  // If loading the requested trip failed, show an error with a retry path
  // instead of an endless skeleton. requestedTripId scopes the error to the
  // trip that actually failed (the slice clears it when a new fetch starts),
  // so a stale error never blocks a different trip, and a failed background
  // refetch (when valid data is already shown) keeps the page visible.
  if (isError && requestedTripId === tripId && !isTripReady) {
    return (
      <Page
        fullWidth
        backAction={{ content: t("business_trips"), url: backUrl }}
        title={t("business_trips")}
      >
        <Card>
          <BlockStack gap="300">
            <Text as="p" tone="critical">
              {t("not_fetch", { defaultValue: "Could not load business trip" })}
            </Text>
            <div>
              <Button onClick={loadData}>
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            </div>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (!isTripReady) {
    return <BusinessTripViewSkeleton />;
  }

  const details = businessTripEntity as BusinessTrip;
  const userDisplay =
    typeof details.user === "object" ? details.user?.name : details.user;
  const substituteDisplay =
    typeof details.signatureUser === "object"
      ? (details.signatureUser as any)?.name
      : details.signatureUser || "-";

  const file = details.files?.[0];
  // A trip whose main document was annulled is set to 'cancelled' by the
  // backend (the file itself keeps isSigned=true — annulment marks, never
  // unsigns). Report/generate/leave actions must stay off from here on: the
  // authorising document is void even though the underlying flags look fine.
  const isTripCancelled =
    details.status === "cancelled" || !!(file as any)?.annulledAt;
  const reportFile = details.reportFile;
  const hasTripResults = !!(
    details.tripResults != null && String(details.tripResults).trim() !== ""
  );
  const hasComments = !!(
    details.comments != null && String(details.comments).trim() !== ""
  );
  const hasAnyReportData = hasTripResults || hasComments;
  const hasCompleteReportData = hasTripResults && hasComments;
  const {
    id: fileId,
    isSigned,
    signersList,
    templateId,
    name: fileName,
    title: fileTitle,
  } = file || {};
  const reportFileName =
    typeof reportFile === "object" && reportFile
      ? (reportFile as any).name || (reportFile as any).title
      : "";
  const reportFileId =
    (details as any).reportFileId ??
    (reportFile && typeof reportFile === "object"
      ? (reportFile as any).id ?? (reportFile as any).file?.id
      : undefined);
  const reportFileIsSigned =
    reportFile && typeof reportFile === "object" ? !!(reportFile as any).isSigned : false;
  const reportSignersList =
    reportFile && typeof reportFile === "object" ? (reportFile as any).signersList ?? [] : [];
  const reportDownloadUrl =
    companyId && reportFileId
      ? `${API_URL}/${companyId}/files/download/${reportFileId}`
      : "";
  const tripOwnerId =
    typeof details.user === "object"
      ? (details.user as any)?.id
      : (details.user as string) || null;
  const isTripCreator = !!tripOwnerId && role?.user === tripOwnerId;
  const tripUser =
    typeof details.user === "object"
      ? (details.user as any)?.id
      : (details.user as string) || role?.user;
  

  const downloadUrl = fileId
    ? `${API_URL}/${companyId}/files/download/${fileId}`
    : "";

  const handleClickSign = (fId: string) => {
    dispatch(setModalxComponent("ClickToSignConfirm"));
    dispatch(setModalxComponentProps({ fileId: fId, tripId, companyId }));
  };

  const handleDeclineSign = (fId: string) => {
    dispatch(setModalxComponent("DeclineClickSignConfirm"));
    dispatch(setModalxComponentProps({ fileId: fId, tripId, companyId }));
  };

  const handleAnnulReport = () => {
    dispatch(setModalxComponent("AnnulReportConfirm"));
    dispatch(setModalxComponentProps({ tripId, companyId }));
  };

  const handleEditFile = (fId: string) => {
    dispatch(setModalxComponent("BusinessTripDocumentForm"));
    dispatch(
      setModalxComponentProps({
        id: fId,
        subject: "business trip",
        user: tripUser,
        businessTrip: tripId,
        businessTripFileType: "main_trip",
      })
    );
  };

  const handlePdfView = (fId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponentProps({ id: fId, companyId }));
  };

  const TemplateEditNode =
    fileId && !isSigned && isTripCreator ? (
      <Button onClick={() => handleEditFile(fileId!)}>{t("edit")}</Button>
    ) : null;

  const handleEditReportDocument = (fId: string) => {
    dispatch(setModalxComponent("BusinessTripDocumentForm"));
    const tripUser =
      businessTripEntity && typeof businessTripEntity.user === "object"
        ? businessTripEntity.user.id
        : (businessTripEntity?.user as string) || role?.user;
    dispatch(
      setModalxComponentProps({
        id: fId,
        user: tripUser,
        subject: "business trip",
        signType: "Click",
        managerSigner: role?.manager,
        businessTrip: tripId,
        businessTripFileType: "trip_report",
      })
    );
  };

  const ReportEditDocumentNode =
    reportFileId && !reportFileIsSigned && isTripCreator && !isTripCancelled ? (
      <Button onClick={() => handleEditReportDocument(reportFileId)}>
        {t("edit_document")}
      </Button>
    ) : null;

  const handleAddLeaveRequest = () => {
    const leaveSource = resolveTemplateSource(
      companyEntity?.businessTripLeaveTemplateSource,
      companyEntity?.businessTripLeaveTemplate
    );
    dispatch(setModalxComponent("BusinessTripDocumentForm"));
    dispatch(
      setModalxComponentProps({
        user: tripUser,
        subject: "business trip",
        signType: "Click",
        managerSigner: role?.manager,
        businessTrip: tripId,
        businessTripFileType: "leaves_during_trip",
        ...(leaveSource === "custom" && companyEntity?.businessTripLeaveTemplate
          ? { lockedTemplateId: companyEntity.businessTripLeaveTemplate }
          : {}),
      })
    );
  };

  const handleEditLeaveFile = (fId: string) => {
    dispatch(setModalxComponent("BusinessTripDocumentForm"));
    dispatch(
      setModalxComponentProps({
        id: fId,
        user: tripUser,
        subject: "user",
        signType: "Click",
        managerSigner: role?.manager,
        businessTrip: tripId,
        businessTripFileType: "leaves_during_trip",
      })
    );
  };

  // Why the edit button is off, as text: a tooltip on a disabled button never shows on a touch screen.
  const editDisabledReason = isSigned
    ? t("document_is_signed_edit_is_not_allowed")
    : !isTripCreator
      ? t("only_trip_creator_can_edit_trip")
      : undefined;

  // Sign and decline are the buttons people come to this page for, so they are full size.
  const signButtonSize = isPhone ? "large" : "medium";

  const pageTitle =
    details.dateFrom && details.dateTo ? (
      <>
        <PrettyDate dateString={details.dateFrom} /> {" – "}{" "}
        <PrettyDate dateString={details.dateTo} />
        {details.placeOfBusinessTrip ? ` ${details.placeOfBusinessTrip}` : ""}
      </>
    ) : (
      details.placeOfBusinessTrip || t("business_trip")
    );

  const DetailRow = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="grid grid-cols-1 gap-1 py-3 border-b border-gray-200 last:border-b-0 sm:grid-cols-[minmax(140px,200px)_1fr] sm:items-baseline sm:gap-4">
      <span className="text-sm font-semibold text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 break-words">{value ?? "-"}</span>
    </div>
  );

  const disabledReason = (reason: string) => (
    <Text as="p" variant="bodySm" tone="subdued">
      {reason}
    </Text>
  );

  const getStepStatus = (stepNumber: number): "green" | "yellow" | "grey" => {
    const hasFile = (details.files?.length ?? 0) > 0;
    if (stepNumber === 1) {
      if (isSigned) return "green";
      return "yellow"; // Step 1 in progress (build/sign doc) – never grey
    }
    if (stepNumber === 2) {
      if (hasCompleteReportData) return "green"; // form fully filled = completed
      if (isSigned || hasAnyReportData) return "yellow";
      return "grey";
    }
    if (stepNumber === 3) {
      if (reportFileIsSigned) return "green";
      if (hasCompleteReportData) return "yellow";
      return "grey";
    }
    return "grey";
  };

  const renderStepConnector = () => (
    <div className="connector-wrapper">
      <div className="vertical-line" />
      <div className="arrow-down" />
    </div>
  );

  // On a phone the button would share the title's row and squeeze the title, so it sits under the header, full width.
  const editButton = (
    <Button
      fullWidth={isPhone}
      disabled={!!editDisabledReason}
      onClick={handleEditClick}
    >
      {t("edit_business_trip")}
    </Button>
  );

  return (
    <Page
      fullWidth
      backAction={{
        content: t("business_trips"),
        url:
          source === "profile"
            ? `/app/${companyId}/profile/${role?.user}/business-trips`
            : `/app/${companyId}/business-trips`,
      }}
      title={pageTitle}
      titleMetadata={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isLoading ? (
            <Spinner size="small" />
          ) : details.status ? (
            <Badge
              tone={
                details.status === "draft"
                  ? "info"
                  : getBusinessTripStatusTone(details.status)
              }
            >
              {t(`status_${details.status}`)}
            </Badge>
          ) : null}
          <AuditLogPopover
            companyId={companyId!}
            subjectType="BusinessTrip"
            subjectId={tripId}
            label={t("history", { defaultValue: "History" })}
          />
        </div>
      }
      subtitle={editDisabledReason}
      primaryAction={isPhone ? undefined : editButton}
    >
      {isPhone && (
        <div className={`mb-4 ${PHONE_PAGE_GUTTER}`}>{editButton}</div>
      )}
      <div className="stack-narrow-section-first">
        <Layout>
          <Layout.Section>
            <div className="space-y-6">
              {isTripCancelled && (
                <Banner tone="critical" title={t("business_trip_cancelled_banner_title")}>
                  <p>{t("business_trip_cancelled_banner_body")}</p>
                </Banner>
              )}
              {/* Business trip details */}
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    {t("details")}
                  </Text>
                  <div className="flex flex-col">
                    {details.status != null && details.status !== undefined && (
                      <DetailRow
                        label={t("status")}
                        value={t(`status_${details.status}`)}
                      />
                    )}
                    <DetailRow
                      label={t("date_from")}
                      value={<PrettyDate dateString={details.dateFrom || ""} />}
                    />
                    <DetailRow
                      label={t("date_to")}
                      value={<PrettyDate dateString={details.dateTo || ""} />}
                    />
                    <DetailRow label={t("user")} value={userDisplay} />
                    <DetailRow
                      label={t("country")}
                      value={details.country ? getCountryName(details.country, t) : "-"}
                    />
                    <DetailRow
                      label={t("city")}
                      value={details.city || "-"}
                    />
                    <DetailRow
                      label={t("transport_used")}
                      value={details.transportUsed}
                    />
                    <DetailRow
                      label={t("advance_for_expenses")}
                      value={
                        details.advanceMoney != null
                          ? `${details.advanceMoney} Eur`
                          : undefined
                      }
                    />
                    <DetailRow
                      label={t("substitute_person")}
                      value={substituteDisplay}
                    />
                    {details.purpose && (
                      <DetailRow
                        label={t("purpose")}
                        value={
                          <Text as="span" variant="bodyMd">
                            {details.purpose}
                          </Text>
                        }
                      />
                    )}
                  </div>
                </BlockStack>
              </Card>

              {/* Leaves during trip */}
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    {t("leaves_during_trip")}
                  </Text>
                  <BlockStack gap="300">
                    {!isTripCreator ? (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {t("only_trip_creator_can_edit_trip")}
                      </Text>
                    ) : isTripCancelled ? (
                      <>
                        <InlineStack>
                          <Button disabled variant="primary">
                            {t("generate_leave_document")}
                          </Button>
                        </InlineStack>
                        {disabledReason(t("business_trip_cancelled_banner_title"))}
                      </>
                    ) : reportFileIsSigned ? (
                      <>
                        <InlineStack>
                          <Button disabled variant="primary">
                            {t("generate_leave_document")}
                          </Button>
                        </InlineStack>
                        {disabledReason(t("document_is_signed_edit_is_not_allowed"))}
                      </>
                    ) : (
                      <InlineStack>
                        <Button
                          onClick={handleAddLeaveRequest}
                          variant="primary"
                        >
                          {t("generate_leave_document")}
                        </Button>
                      </InlineStack>
                    )}
                  </BlockStack>
                  {(details.leaveFiles?.length ?? 0) > 0 ? (
                    <BlockStack gap="400">
                      {(details.leaveFiles ?? []).map((leaveFile: any, index: number) => {
                        const lfId = leaveFile.id;
                        const lfSignersList = leaveFile.signersList ?? [];
                        const lfIsSigned = !!leaveFile.isSigned;
                        const lfDownloadUrl = lfId
                          ? `${API_URL}/${companyId}/files/download/${lfId}`
                          : "";
                        const lfName = leaveFile.name || leaveFile.title || t("leaves_during_trip");
                        return (
                          <BlockStack key={lfId || `leave-file-${index}`} gap="400">
                            <BlockStack gap="300">
                              <div className="Polaris-Link">
                                <Link
                                  url={lfDownloadUrl}
                                  target="_blank"
                                  removeUnderline
                                  monochrome
                                >
                                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                                    {lfName}
                                  </Text>
                                </Link>
                              </div>
                              <InlineStack gap="200">
                                {!lfIsSigned && isTripCreator && !isTripCancelled && (
                                  <Button
                                    size="slim"
                                    onClick={() => handleEditLeaveFile(lfId)}
                                  >
                                    {t("edit")}
                                  </Button>
                                )}
                                <Button
                                  size="slim"
                                  icon={<ViewIcon />}
                                  onClick={() => handlePdfView(lfId)}
                                >
                                  {t("view")}
                                </Button>
                              </InlineStack>
                            </BlockStack>
                            {lfSignersList.length > 0 && (
                              <BlockStack gap="200">
                                {(lfSignersList as any[]).map((signer: any) => (
                                  <div
                                    key={signer.id}
                                    className="py-3 border-b border-gray-200 last:border-b-0"
                                  >
                                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                                      <Text as="span" variant="bodySm" fontWeight="medium">
                                        {signer?.user?.name}
                                      </Text>
                                      {(signer.status === "signed" &&
                                        (signer.signing_time || signer.updatedAt)) ||
                                      (signer.status === "pending" && signer.createdAt) ||
                                      (signer.status === "rejected" &&
                                        (signer.updatedAt || signer.signing_time)) ? (
                                        <Text as="span" variant="bodySm" tone="subdued">
                                          <PrettyDate
                                            dateString={
                                              signer.status === "signed"
                                                ? signer.signing_time || signer.updatedAt
                                                : signer.status === "pending"
                                                ? signer.createdAt
                                                : signer.updatedAt || signer.signing_time
                                            }
                                            showTime
                                          />
                                        </Text>
                                      ) : null}
                                      {signer.status === "signed" ? (
                                        <Badge tone="success">{t("signed")}</Badge>
                                      ) : signer.status === "pending" ? (
                                        <Badge tone="attention">{t("pending")}</Badge>
                                      ) : signer.status === "rejected" ? (
                                        <Badge tone="critical">{t("declined")}</Badge>
                                      ) : null}
                                    </InlineStack>
                                    {signer?.user?.id === role?.user &&
                                      signer.status === "pending" && (
                                        <div className="mt-2">
                                          <Button
                                            size={signButtonSize}
                                            onClick={() => handleClickSign(lfId)}
                                            accessibilityLabel="Sign"
                                          >
                                            {t("sign")}
                                          </Button>
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </BlockStack>
                            )}
                          </BlockStack>
                        );
                      })}
                    </BlockStack>
                  ) : (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t("leaves_during_trip_empty_hint")}
                    </Text>
                  )}
                </BlockStack>
              </Card>

              {/* Business trip report data */}
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    {t("business_trip_report")}
                  </Text>
                  {!hasAnyReportData ? (
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {t("business_trip_report_empty_hint")}
                    </Text>
                  ) : (
                    <div className="flex flex-col">
                      <DetailRow
                        label={t("trip_results")}
                        value={details.tripResults}
                      />
                      <DetailRow label={t("comments")} value={details.comments} />
                    </div>
                  )}
                </BlockStack>
              </Card>

              {/* Business trip expenses */}
              {tripId ? (
                <BusinessTripExpenses
                  tripId={tripId}
                  isLocked={reportFileIsSigned || isTripCancelled}
                  canManageExpenses={
                    isTripCreator ||
                    !!role?.isMaster ||
                    permissionsGroups.includes('Access business trips')
                  }
                />
              ) : null}
            </div>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <div className="flex flex-col business-trip-steps-column">
              {/* Step 1: Business trip document – generate, file + edit, signers (one block) */}
              <div className="card-wrapper">
                <div className={`card-box border-${getStepStatus(1)}`}>
                <Card>
                  <BlockStack gap="400">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                        1
                      </span>
                      <Text as="h2" variant="headingSm">
                        {t("generate_business_trip_document")}
                      </Text>
                    </div>
                    {(details.files?.length ?? 0) === 0 ? (
                      <BlockStack gap="300">
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {t("business_trip_document_empty_hint")}
                        </Text>
                        {isTripCreator ? (
                          <>
                            <Button
                              onClick={handleBuildPdf}
                              variant="primary"
                              loading={isGeneratingMain}
                            >
                              {t("files_build_pdf")}
                            </Button>
                            {mainTemplateError && (
                              <Text as="p" variant="bodySm" tone="critical">
                                {t(mainTemplateError)}
                              </Text>
                            )}
                          </>
                        ) : (
                          <Text as="p" variant="bodySm" tone="subdued">
                            {t("only_trip_creator_can_edit_trip")}
                          </Text>
                        )}
                      </BlockStack>
                    ) : (
                      <BlockStack gap="400">
                        <BlockStack gap="300">
                          <div className="Polaris-Link">
                            <Link
                              url={downloadUrl}
                              target="_blank"
                              removeUnderline
                              monochrome
                            >
                              <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {fileName || fileTitle}
                              </Text>
                            </Link>
                          </div>
                          <InlineStack gap="200">
                            {TemplateEditNode}
                            <Button
                              size="slim"
                              icon={<ViewIcon />}
                              onClick={() => handlePdfView(fileId!)}
                            >
                              {t("view")}
                            </Button>
                          </InlineStack>
                        </BlockStack>
                        {(signersList?.length ?? 0) === 0 ? (
                          <Text as="p" variant="bodyMd" tone="subdued">
                            {t("business_trip_signers_empty_hint")}
                          </Text>
                        ) : (
                          <BlockStack gap="300">
                            {(signersList ?? []).map((signer: any) => (
                              <div
                                key={signer.id}
                                className="py-3 border-b border-gray-200 last:border-b-0"
                              >
                                <InlineStack gap="200" blockAlign="center" wrap={false}>
                                  <Text as="span" variant="bodyMd" fontWeight="medium">
                                    {signer?.user?.name}
                                  </Text>
                                  {(signer.status === "signed" &&
                                    (signer.signing_time || signer.updatedAt)) ||
                                  (signer.status === "pending" && signer.createdAt) ||
                                  (signer.status === "rejected" &&
                                    (signer.updatedAt || (signer as any).signing_time)) ? (
                                    <Text as="span" variant="bodySm" tone="subdued">
                                      <PrettyDate
                                        dateString={
                                          signer.status === "signed"
                                            ? (signer as any).signing_time || signer.updatedAt
                                            : signer.status === "pending"
                                            ? signer.createdAt
                                            : signer.updatedAt || (signer as any).signing_time
                                        }
                                        showTime
                                      />
                                    </Text>
                                  ) : null}
                                  {signer.status === "signed" ? (
                                    <Badge tone="success">{t("signed")}</Badge>
                                  ) : signer.status === "pending" ? (
                                    <Badge tone="attention">{t("pending")}</Badge>
                                  ) : signer.status === "rejected" ? (
                                    <Badge tone="critical">{t("declined")}</Badge>
                                  ) : null}
                                </InlineStack>
                                {signer?.user?.id === role?.user &&
                                  signer.status === "pending" && (
                                    <div className="mt-2">
                                      <Button
                                        size={signButtonSize}
                                        onClick={() => handleClickSign(fileId!)}
                                        accessibilityLabel="Sign"
                                      >
                                        {t("sign")}
                                      </Button>
                                    </div>
                                  )}
                              </div>
                            ))}
                            {/* Main trip document: substitute (responsible user) is added as signer on backend; no "Request manager sign" button */}
                            {isSigned && (
                              <InlineStack gap="200">
                                <Badge tone="success">{t("signed")}</Badge>
                              </InlineStack>
                            )}
                          </BlockStack>
                        )}
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
                </div>
                {renderStepConnector()}
              </div>

              {/* Step 2: Report form – always visible, border by status */}
              <div className="card-wrapper">
                <div className={`card-box border-${getStepStatus(2)}`}>
                <Card>
                  <BlockStack gap="400">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                        2
                      </span>
                      <Text as="h2" variant="headingSm">
                        {t("report_form")}
                      </Text>
                    </div>
                    {!isSigned ? (
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {t("complete_previous_step")}
                      </Text>
                    ) : !hasAnyReportData ? (
                      <BlockStack gap="300">
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {t("business_trip_report_empty_hint")}
                        </Text>
                        {isTripCancelled ? (
                          <>
                            <Button disabled>{t("create_report")}</Button>
                            {disabledReason(t("business_trip_cancelled_banner_title"))}
                          </>
                        ) : reportFileIsSigned ? (
                          <>
                            <Button disabled>{t("create_report")}</Button>
                            {disabledReason(t("document_is_signed_edit_is_not_allowed"))}
                          </>
                        ) : isTripCreator ? (
                          <Button onClick={handleCreateReport} variant="primary">
                            {t("create_report")}
                          </Button>
                        ) : (
                          <Text as="p" variant="bodySm" tone="subdued">
                            {t("only_trip_creator_can_edit_trip")}
                          </Text>
                        )}
                      </BlockStack>
                    ) : (
                      <BlockStack gap="300">
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {t("business_trip_report_fill_then_generate_hint")}
                        </Text>
                        {isTripCancelled ? (
                          <>
                            <Button disabled>{t("edit_report")}</Button>
                            {disabledReason(t("business_trip_cancelled_banner_title"))}
                          </>
                        ) : reportFileIsSigned ? (
                          <>
                            <Button disabled>{t("edit_report")}</Button>
                            {disabledReason(t("document_is_signed_edit_is_not_allowed"))}
                          </>
                        ) : isTripCreator ? (
                          <Button onClick={handleEditReport}>
                            {t("edit_report")}
                          </Button>
                        ) : (
                          <Text as="p" variant="bodySm" tone="subdued">
                            {t("only_trip_creator_can_edit_trip")}
                          </Text>
                        )}
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
                </div>
                {renderStepConnector()}
              </div>

              {/* Step 3: Report document – always visible, border by status */}
              <div className="card-wrapper">
                <div className={`card-box border-${getStepStatus(3)}`}>
                <Card>
                  <BlockStack gap="400">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                        3
                      </span>
                      <Text as="h2" variant="headingSm">
                        {t("report_document")}
                      </Text>
                    </div>
                    {!hasCompleteReportData ? (
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {t("complete_previous_step")}
                      </Text>
                    ) : !reportFile ? (
                      <BlockStack gap="300">
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {t("business_trip_report_fill_then_generate_hint")}
                        </Text>
                        {isTripCancelled ? (
                          <>
                            <Button disabled variant="primary">
                              {t("generate_report_document")}
                            </Button>
                            {disabledReason(t("business_trip_cancelled_banner_title"))}
                          </>
                        ) : isTripCreator ? (
                          <>
                            <Button
                              onClick={handleGenerateReportDocument}
                              variant="primary"
                              loading={isGeneratingReport}
                            >
                              {t("generate_report_document")}
                            </Button>
                            {reportTemplateError && (
                              <Text as="p" variant="bodySm" tone="critical">
                                {t(reportTemplateError)}
                              </Text>
                            )}
                          </>
                        ) : (
                          <Text as="p" variant="bodySm" tone="subdued">
                            {t("only_trip_creator_can_edit_trip")}
                          </Text>
                        )}
                      </BlockStack>
                    ) : (
                      <BlockStack gap="400">
                        <BlockStack gap="300">
                          <div className="Polaris-Link">
                            <Link
                              url={reportDownloadUrl}
                              target="_blank"
                              removeUnderline
                              monochrome
                            >
                              <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {reportFileName || t("business_trip_report")}
                              </Text>
                            </Link>
                          </div>
                          <InlineStack gap="200">
                            {ReportEditDocumentNode}
                            {reportFileId && (
                              <Button
                                size="slim"
                                icon={<ViewIcon />}
                                onClick={() => handlePdfView(reportFileId)}
                              >
                                {t("view")}
                              </Button>
                            )}
                            {reportFileIsSigned &&
                              !isTripCancelled &&
                              (role?.isMaster ||
                                permissionsGroups.includes("Master")) && (
                              <Button
                                size="slim"
                                tone="critical"
                                onClick={handleAnnulReport}
                              >
                                {t("annul_report")}
                              </Button>
                            )}
                          </InlineStack>
                        </BlockStack>
                        {(reportSignersList?.length ?? 0) > 0 && (
                          <BlockStack gap="300">
                            {(reportSignersList ?? []).map((signer: any) => (
                              <div
                                key={signer.id}
                                className="py-3 border-b border-gray-200 last:border-b-0"
                              >
                                <InlineStack gap="200" blockAlign="center" wrap={false}>
                                  <Text as="span" variant="bodyMd" fontWeight="medium">
                                    {signer?.user?.name}
                                  </Text>
                                  {(signer.status === "signed" &&
                                    (signer.signing_time || signer.updatedAt)) ||
                                  (signer.status === "pending" && signer.createdAt) ||
                                  (signer.status === "rejected" &&
                                    (signer.updatedAt || (signer as any).signing_time)) ? (
                                    <Text as="span" variant="bodySm" tone="subdued">
                                      <PrettyDate
                                        dateString={
                                          signer.status === "signed"
                                            ? (signer as any).signing_time || signer.updatedAt
                                            : signer.status === "pending"
                                            ? signer.createdAt
                                            : signer.updatedAt || (signer as any).signing_time
                                        }
                                        showTime
                                      />
                                    </Text>
                                  ) : null}
                                  {signer.status === "signed" ? (
                                    <Badge tone="success">{t("signed")}</Badge>
                                  ) : signer.status === "pending" ? (
                                    <Badge tone="attention">{t("pending")}</Badge>
                                  ) : signer.status === "rejected" ? (
                                    <Badge tone="critical">{t("declined")}</Badge>
                                  ) : null}
                                </InlineStack>
                                {signer.status === "rejected" &&
                                  (signer as any).declineReason && (
                                    <div className="mt-2">
                                      <Text as="p" variant="bodySm" tone="critical">
                                        {t("reason_for_decline_signature")}:{" "}
                                        {(signer as any).declineReason}
                                      </Text>
                                    </div>
                                  )}
                                {signer?.user?.id === role?.user &&
                                  signer.status === "pending" &&
                                  reportFileId &&
                                  !isTripCancelled && (
                                    <div className="mt-2">
                                      <InlineStack gap="400">
                                        <Button
                                          size={signButtonSize}
                                          onClick={() => handleClickSign(reportFileId)}
                                          accessibilityLabel="Sign"
                                        >
                                          {t("sign")}
                                        </Button>
                                        <Button
                                          size={signButtonSize}
                                          tone="critical"
                                          onClick={() => handleDeclineSign(reportFileId)}
                                          accessibilityLabel={t("decline")}
                                        >
                                          {t("decline")}
                                        </Button>
                                      </InlineStack>
                                    </div>
                                  )}
                              </div>
                            ))}
                            {reportFileIsSigned && (
                              <InlineStack gap="200">
                                <Badge tone="success">{t("signed")}</Badge>
                              </InlineStack>
                            )}
                          </BlockStack>
                        )}
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
                </div>
              </div>
            </div>
          </Layout.Section>
        </Layout>
      </div>
    </Page>
  );
}

export default BusinessTripContainer;
